import axios from "axios";
import Services, {subscriptionKeys} from "./Services";
import {securexHref} from "../utils/strings";
import Subscriptions from "../Subscriptions";

export const axiosInstance = axios.create({});

const privateIntelProxy = "/iroh/private-intel";

export const requestObject = (
  method,
  api,
  data,
  {
    requestOptions = {},
    queryParams = {},
    serverOptions = {
      privateURL: false,
      privateProxyURL: false,
      publicURL: false,
      orbitalURL: false
    },
    noAuth = false
  } = {}
) => {
  const {
    privateURL,
    privateProxyURL,
    publicURL,
    orbitalURL,
    serverURL,
    portalURL
  } = serverOptions;

  let url = `${Services.apiHost}${api}`;

  if (serverURL) {
    url = `${serverURL}${api}`;
  } else if (privateURL) {
    url = `${Services.privateURL}${api}`;
  } else if (privateProxyURL) {
    url = `${Services.apiHost}${privateIntelProxy}${api}`;
  } else if (publicURL) {
    url = `${Services.publicURL}${api}`;
  } else if (orbitalURL) {
    url = `${Services.orbitalURL}${api}`;
  } else if (portalURL) {
    url = `${securexHref()}${api}`;
  }

  if (["put", "post", "patch"].includes(method)) {
    if (queryParams && Object.keys(queryParams)) {
      const params = new URLSearchParams(queryParams),
        str = params.toString();

      if (str) {
        url += `?${params.toString()}`;
      }
    }
  }

  const headers = {
    //"content-type": "application/json;charset=utf-8",
    accept: "application/json"
  };

  if (!noAuth) {
    if (Services.credentials) {
      headers.authorization = `Bearer ${Services.credentials.replace(
        "Bearer ",
        ""
      )}`;

      headers["X-CLIENT-APP"] = "ribbon-services";
    } else {
      return null;
    }
  }

  return {
    url,
    method,
    [method === "get" ? "params" : "data"]: data,
    headers,
    adapter: Services.axiosAdapter, // can override call mechanism
    ...requestOptions
  };
};

export const request = (...args) => {
  if (!Services.host) {
    return {
      promise: Promise.reject(new Error("No services host set.")).catch(e => {
        console.error(e);
        throw e;
      }),
      cancel: () => {}
    };
  }

  const CancelToken = axios.CancelToken,
    source = CancelToken.source(),
    requestData = requestObject(...args);

  // If there's no request data, means there's no auth and there should be, so don't make the call.
  if (!requestData) {
    return {
      promise: Promise.reject(
        new Error(
          "No authorization set. If Ribbon authorization has recently been revoked, this error can be safely ignored."
        )
      ).catch(e => {
        throw e;
      }),
      cancel: () => {}
    };
  }

  requestData.cancelToken = source.token;

  const promise = axiosInstance(requestData).catch(thrown => {
    const {response = {}} = thrown;

    if (axios.isCancel(thrown)) {
      throw new RequestCancelledError();
    }

    const {status} = response,
      errorData = {
        type: "services",
        status,
        response,
        thrown,
        requestArgs: args
      };

    Subscriptions.publish(subscriptionKeys.servicesError, errorData);

    const additional = response && response.config && response.config.url;

    let message = thrown.message;
    if (additional) {
      message += `\n${additional}\n`;
    }
    throw new ServicesError(message, errorData);
  });

  return {
    promise,
    cancel: source.cancel
  };
};

export const getRequest = (...args) => {
  return request("get", ...args);
};

export const patchRequest = (...args) => {
  return request("patch", ...args);
};

export const postRequest = (...args) => {
  return request("post", ...args);
};

export const putRequest = (...args) => {
  return request("put", ...args);
};

export const deleteRequest = (...args) => {
  return request("delete", ...args);
};

export class RequestCancelledError extends Error {
  constructor(message) {
    super(message);
    this.name = "RequestCancelled";
  }
}

export class ServicesError extends Error {
  constructor(message, data) {
    super(message);
    this.name = "ServicesError";
    this.data = data;

    Object.assign(this, data);
  }
}
